import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AppLocalStorageService } from 'src/app/shared/services/app-local-storage.service';

const DESKTOP_MIN_WIDTH = 1025;
const STORAGE_KEY = 'sidenav_state';
const LEGACY_STORAGE_KEY = 'sidenav-fixed';

@Injectable({
  providedIn: 'root',
})
export class SidenavService {
  private sidenavStatus: SidenavStatus;
  private sidenavSubject: BehaviorSubject<SidenavStatus>;

  constructor(
    private authService: AuthService,
    private localStorageService: AppLocalStorageService
  ) {
    this.localStorageService.removeItem(LEGACY_STORAGE_KEY);
    this.sidenavStatus = { closed: true };
    this.sidenavSubject = new BehaviorSubject(this.sidenavStatus);
  }

  start(): Subscription {
    return new Subscription();
  }

  restaurarPreferenciaDesktop(): void {
    if (!this.isDesktop()) {
      this.publicarEstado(true);
      return;
    }

    const codUsuario = this.getCodUsuarioAtual();
    if (!codUsuario) {
      this.publicarEstado(false);
      return;
    }

    const preferencias = this.getPreferencias();
    const preferencia = preferencias.users[codUsuario] ?? 'open';
    if (!preferencias.users[codUsuario]) {
      preferencias.users[codUsuario] = preferencia;
      this.localStorageService.setItem(STORAGE_KEY, preferencias);
    }

    this.publicarEstado(preferencia === 'closed');
  }

  alternarPreferenciaDesktop(): void {
    if (!this.isDesktop()) return;

    const closed = !this.sidenavStatus.closed;
    const codUsuario = this.getCodUsuarioAtual();
    if (codUsuario) {
      const preferencias = this.getPreferencias();
      preferencias.users[codUsuario] = closed ? 'closed' : 'open';
      this.localStorageService.setItem(STORAGE_KEY, preferencias);
    }

    this.publicarEstado(closed);
  }

  definirEstadoTransitorio(closed: boolean): void {
    this.publicarEstado(closed);
  }

  getStatus(): BehaviorSubject<SidenavStatus> {
    return this.sidenavSubject;
  }

  private isDesktop(): boolean {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  private getCodUsuarioAtual(): string | null {
    if (!this.authService.isTokenValid()) return null;

    const codUsuario = this.authService.usuarioLogado()?.CODUSUARIO;
    if (!Number.isInteger(codUsuario) || !codUsuario || codUsuario < 0) return null;

    return codUsuario.toString();
  }

  private getPreferencias(): SidenavStorage {
    const item = this.localStorageService.getItem(STORAGE_KEY);
    if (!item) return { version: 1, users: {} };

    try {
      const preferences = JSON.parse(item) as unknown;
      if (!this.isStorageValido(preferences)) return { version: 1, users: {} };

      const users = Object.entries(preferences.users).reduce<Record<string, SidenavDesktopState>>((accumulator, [codUsuario, state]) => {
        if (state === 'open' || state === 'closed') {
          accumulator[codUsuario] = state;
        }
        return accumulator;
      }, {});

      return { version: 1, users };
    } catch {
      return { version: 1, users: {} };
    }
  }

  private isStorageValido(storage: unknown): storage is SidenavStorage {
    if (!storage || typeof storage !== 'object') return false;

    const { version, users } = storage as Partial<SidenavStorage>;
    return version === 1 && !!users && typeof users === 'object' && !Array.isArray(users);
  }

  private publicarEstado(closed: boolean): void {
    this.sidenavStatus = { closed };
    this.sidenavSubject.next(this.sidenavStatus);
  }
}

export interface SidenavStatus {
  closed: boolean;
}

type SidenavDesktopState = 'open' | 'closed';

interface SidenavStorage {
  version: 1;
  users: Record<string, SidenavDesktopState>;
}
